import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Check, Eye, EyeOff } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('This reset link is missing its token. Please request a new one.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords don’t match');
    setBusy(true);
    setError('');
    try {
      const r = await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: pw }),
      });
      // Auto-login
      localStorage.setItem('token', r.token);
      localStorage.setItem('user', JSON.stringify(r.user));
      setDone(true);
      toast.success('Password updated', { description: 'You’re signed in.' });
      setTimeout(() => { window.location.href = '/'; }, 1200);
    } catch (err) {
      setError(err.message || 'That reset link is invalid or expired.');
      toast.error('Reset failed', { description: err.message });
    }
    setBusy(false);
  };

  return (
    <div className="reset-page" style={styles.page} data-testid="reset-password-page">
      <div style={styles.card}>
        <div style={styles.iconWrap}><Lock size={28} /></div>
        <h1 style={styles.title}>Choose a new password</h1>
        <p style={styles.sub}>
          {done ? 'All done — redirecting…' : 'At least 8 characters. Use something you\'ll remember.'}
        </p>

        {error && !done && (
          <div style={styles.error} data-testid="reset-error">{error}</div>
        )}

        {!done && (
          <form onSubmit={submit} style={styles.form}>
            <label style={styles.label}>
              New password
              <div style={styles.inputWrap}>
                <input
                  type={show ? 'text' : 'password'}
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  style={styles.input}
                  autoComplete="new-password"
                  data-testid="reset-pw"
                  required minLength={8}
                />
                <button type="button" onClick={() => setShow(s => !s)} style={styles.eye} aria-label="toggle visibility">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <label style={styles.label}>
              Confirm password
              <div style={styles.inputWrap}>
                <input
                  type={show ? 'text' : 'password'}
                  value={pw2} onChange={(e) => setPw2(e.target.value)}
                  placeholder="Retype it"
                  style={styles.input}
                  autoComplete="new-password"
                  data-testid="reset-pw2"
                  required minLength={8}
                />
              </div>
            </label>
            <button
              type="submit"
              style={{ ...styles.btn, opacity: (!token || busy) ? 0.55 : 1 }}
              disabled={!token || busy}
              data-testid="reset-submit"
            >
              {busy ? 'Updating…' : 'Update password'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/auth')}
              style={styles.linkBtn}
            >
              Back to sign in
            </button>
          </form>
        )}

        {done && (
          <div style={styles.successWrap}>
            <div style={styles.checkCircle}><Check size={28} /></div>
            <p style={styles.successText}>Password updated. Signing you in…</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh', background: '#FDFCF8',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20, fontFamily: 'Manrope, sans-serif',
  },
  card: {
    background: '#fff', border: '1px solid #E5E7EB', borderRadius: 24,
    padding: '36px 28px', width: '100%', maxWidth: 420, textAlign: 'center',
    boxShadow: '0 8px 32px rgba(31, 41, 55, 0.06)',
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: 999, margin: '0 auto 16px',
    background: '#E6E8E6', color: '#2C4E46',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  title: { fontFamily: 'Fraunces, serif', fontSize: 26, color: '#1F2937', margin: '0 0 6px', fontWeight: 700 },
  sub: { color: '#6B7280', fontSize: 14, margin: '0 0 20px' },
  error: { background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16, textAlign: 'left' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, color: '#4B5563' },
  inputWrap: { display: 'flex', alignItems: 'center', background: '#FDFCF8', border: '1px solid #E5E7EB', borderRadius: 12, padding: '0 12px' },
  input: { flex: 1, border: 'none', outline: 'none', background: 'transparent', padding: '12px 0', fontSize: 14, color: '#1F2937', fontFamily: 'inherit' },
  eye: { border: 'none', background: 'transparent', color: '#9CA3AF', cursor: 'pointer', padding: 6, display: 'flex' },
  btn: { height: 48, borderRadius: 999, border: 'none', background: '#2C4E46', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  linkBtn: { border: 'none', background: 'transparent', color: '#6B7280', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', marginTop: 4 },
  successWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '12px 0' },
  checkCircle: { width: 56, height: 56, borderRadius: 999, background: '#D1FAE5', color: '#065F46', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  successText: { color: '#065F46', fontWeight: 600, fontSize: 14, margin: 0 },
};

export default ResetPassword;
